import { localizeRoute, type LOCALIZED_PATHS } from '../constants/url-constants';
import { buildCompanyProfileSlugBase, isCompanyProfileSlug } from '../company/company-profile-slug';

type AppLocale = keyof typeof LOCALIZED_PATHS;

/**
 * URL slug for an employer profile. Prefers the slug assigned on the company record
 * (`AlgoliaCompanyRecord.profileSlug`, unique and sticky); callers that only hold a `companyId`
 * get the brand slug derived from it, which is the same value for every company that is not in
 * a collision group (see utils/company/company-profile-slug.ts for how the rare collisions
 * resolve: the record with the most live jobs holds the brand slug, so an id-only link from a
 * job card lands on the right employer in practice).
 */
export function companyProfileSlugFor(companyId: string, profileSlug?: string | null): string {
  const explicit = String(profileSlug ?? '').trim();
  if (explicit && isCompanyProfileSlug(explicit)) return explicit;
  return buildCompanyProfileSlugBase(String(companyId ?? '').trim());
}

/**
 * English logical employer profile path (`urlLogical` / Vike filesystem route `pages/company`).
 */
export function companyProfileLogicalPath(companyId: string, profileSlug?: string | null): string {
  return `/company/${companyProfileSlugFor(companyId, profileSlug)}`;
}

/**
 * Full public path with locale prefix and localized segment
 * (`/fi/tyonantaja/terveystalo`, `/sv/arbetsgivare/terveystalo`, `/en/employer/terveystalo`).
 */
export function companyProfileUrlPath(locale: AppLocale, companyId: string, profileSlug?: string | null): string {
  const logical = companyProfileLogicalPath(companyId, profileSlug);
  return `/${locale}${localizeRoute(logical, locale)}`;
}
